import { Box, Button, VStack, Text, Code, Input } from '@chakra-ui/react';
import { ReactElement, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { PageContext } from 'components/common/PageContext';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { MultiSelectField } from 'components/common/Form/MultiSelectField';
import { ChipInputField } from 'components/common/Form/ChipInputField';
import { EmailChipInputField } from 'components/common/Form/EmailChipInputField';
import Joi from 'joi';
import { MultiSelect } from 'components/common/Multiselect';
import { ChipInput, EmailChipInput } from 'components/common/ChipInput';
import {
  SegmentControl,
  SegmentControlWithContent,
  SegmentItem,
} from 'components/common/SegmentControl';
import { NameList } from 'components/common/NameList';
import { ListIcon, UserIcon } from 'assets/icons/v2';
import { ButtonTextLinkPreview } from 'pages/in-app/styleguide/ButtonTextLinkPreview';
import { IconButtonPreview } from 'pages/in-app/styleguide/IconButtonPreview';
import { RHFChipInputField } from 'components/common/Form/v2/RHFChipInputField';
import { RHFCheckboxField } from 'components/common/Form/v2/RHFCheckboxField';
import { RHFFormInput } from 'components/common/Form/v2/RHFFormInput';
import { Form } from 'components/common/Form/v2/Form/Form';

/**
 * ComponentPreview page - A styleguide/preview page for components
 * Used to preview and test various components in isolation
 */
export const ComponentPreview = (): ReactElement => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [chipValues, setChipValues] = useState<string[]>([]);
  const [emailChipValues, setEmailChipValues] = useState<string[]>([]);
  const [activeSegmentId, setActiveSegmentId] = useState<string>('segment1');
  const [teacherValue, setTeacherValue] = useState<string>('');

  const sampleOptions = [
    { id: '1', label: 'Liam Smith', email: 'liam.smith@sample.com' },
    { id: '2', label: 'Oliver Johnson', email: 'oliver.johnson@example.com' },
    { id: '3', label: 'Ethan Brown', email: 'ethan.brown@example.com' },
    { id: '4', label: 'Liam Johnson', email: 'liam.johnson@example.com' },
    { id: '5', label: 'Mason Davis', email: 'mason.davis@example.com' },
    { id: '6', label: 'Mason Davis 2', email: 'mason.davis2@example.com' },
    { id: '7', label: 'Mason Davis 3', email: 'mason.davis3@example.com' },
    { id: '8', label: 'Mason Davis 4', email: 'mason.davis4@example.com' },
    { id: '9', label: 'Mason Davis 5', email: 'mason.davis5@example.com' },
    { id: '10', label: 'Mason Davis 6', email: 'mason.davis6@example.com' },
  ];

  const handleSelectChange = (values: string[]): void => {
    setSelectedValues(values);
  };

  // Example form schema for react-hook-form
  const formSchema = Joi.object({
    people: Joi.array().items(Joi.string()).min(1).messages({
      'array.min': 'Please select at least one participant',
    }),
    tags: Joi.array().items(Joi.string()).min(1).messages({
      'array.min': 'Please add at least one tag',
    }),
    emails: Joi.array()
      .items(
        Joi.string().email({
          tlds: {
            allow: false,
          },
        })
      )
      .min(1)
      .messages({
        'array.min': 'Please add at least one email address',
        'string.email': 'Please enter valid email addresses',
      }),
  });

  type FormValues = {
    people: string[];
    tags: string[];
    emails: string[];
  };

  const handleFormSubmit = (data: FormValues): void => {
    // eslint-disable-next-line no-console
    console.log('Form submitted with data:', data);
  };

  const rhfContextSampleSchema = Joi.object({
    displayName: Joi.string().min(2).max(80).required().messages({
      'string.empty': errorMessage.empty,
      'string.min': 'Mindestens 2 Zeichen.',
    }),
    notes: Joi.array().items(Joi.string()).min(1).messages({
      'array.min': 'Bitte mindestens einen Eintrag hinzufügen.',
    }),
    acceptTerms: Joi.boolean().valid(true).required().messages({
      'any.only': errorMessage.checkboxRequired,
    }),
  });

  type RhfContextSampleValues = {
    displayName: string;
    notes: string[];
    acceptTerms: boolean;
  };

  const handleRhfContextSampleSubmit = (data: RhfContextSampleValues): void => {
    // eslint-disable-next-line no-console
    console.log('RHF context sample:', data);
  };

  // Segment Control segments with different content
  const segmentItems: SegmentItem[] = [
    {
      id: 'segment1',
      label: 'Manual Invite',
      icon: <ListIcon width="18px" height="18px" />,
      content: (
        <VStack gap="15px" align="stretch">
          <Text textStyle="h4.medium" color="text.primary">
            Manual Invite Content
          </Text>
          <Text textStyle="body.m.regular" color="text.tertiary">
            This is the content for the Manual Invite segment. You can add any content here, such as
            forms, lists, or other components.
          </Text>
          <Input name="manualInvite" />
          <Box bg="background.canvas" p="15px" borderRadius="l">
            <Text textStyle="body.s.medium" color="text.secondary">
              Example content area
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: 'segment2',
      label: 'Bulk Import',
      icon: <ListIcon width="18px" height="18px" />,
      content: (
        <VStack gap="15px" align="stretch">
          <Text textStyle="h4.medium" color="text.primary">
            Bulk Import Content
          </Text>
          <Text textStyle="body.m.regular" color="text.tertiary">
            This is the content for the Bulk Import segment. Different content is shown based on the
            active segment.
          </Text>
          <Box bg="background.canvas" p="15px" borderRadius="l">
            <Text textStyle="body.s.medium" color="text.secondary">
              Bulk import functionality would go here
            </Text>
          </Box>
        </VStack>
      ),
    },
  ];

  const activeSegment = segmentItems.find((segment) => segment.id === activeSegmentId);

  return (
    <PageContext title="Component Preview" hasBackButton>
      <Box w="full" py="4xl" flexGrow={1}>
        <Box
          bg="background.primary"
          p="4xl"
          height="100%"
          flexGrow={1}
          width={{ base: '100%', md: '800px' }}
          maxWidth="100%"
          borderRadius={{ md: '2xl' }}
        >
          <VStack gap="30px" align="stretch">
            <Text textStyle="h2.medium" color="text.primary">
              Component Preview
            </Text>
            <Text textStyle="body.m.regular" color="text.tertiary">
              Styleguide/Vorschau für einzelne Komponenten. Oben: Matrix für{' '}
              <Code fontSize="sm">Button</Code> (Wrapper), <Code fontSize="sm">TextLink</Code> und{' '}
              <Code fontSize="sm">NovaIconButton</Code> nach UI-Kit — gut für Abgleich mit Figma und
              Fokus-Ringe.
            </Text>

            <ButtonTextLinkPreview />

            <IconButtonPreview />

            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary" mt="l">
                Searchable MultiSelectInput
              </Text>
              <MultiSelect
                placeholder="Select Names"
                options={sampleOptions}
                selectedValues={selectedValues}
                onChange={(values) => handleSelectChange(values)}
                searchable
                searchPlaceholder="Personen Suchen..."
                secondaryKey="email"
              />
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                  Current Values:
                </Text>
                <Code
                  p="10px"
                  bg="background.primary"
                  borderRadius="s"
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="12px"
                >
                  {JSON.stringify(selectedValues, null, 2)}
                </Code>
              </Box>
            </VStack>

            {/* SelectCell Component */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary" mt="l">
                SelectCell (Table Cell)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                A compact inline select dropdown component designed for use in table cells. Features
                smaller height, custom styling, and a dropdown arrow icon. Perfect for role
                selection or similar inline editing scenarios in data tables.
              </Text>
              {/* <Box width="100%" maxWidth="200px">
                <SelectCell
                  label={teacherValue || 'Teacher'}
                  value={teacherValue}
                  onChange={setTeacherValue}
                  options={[
                    { value: 'teacher', label: 'Teacher' },
                    { value: 'student', label: 'Student' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                />
              </Box> */}
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                  Current Value:
                </Text>
                <Code
                  p="10px"
                  bg="background.primary"
                  borderRadius="s"
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="12px"
                >
                  {JSON.stringify(teacherValue || 'Teacher', null, 2)}
                </Code>
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  Features:
                </Text>
                <VStack gap="xs" align="stretch">
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Compact design optimized for table cells (28px height)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Custom dropdown arrow icon (8x8px SVG)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Supports controlled and uncontrolled modes
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Customizable options array
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Matches Figma design specifications
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Uses Chakra UI v3 styling tokens
                  </Text>
                </VStack>
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  Usage Example:
                </Text>
                <Code
                  p="10px"
                  bg="background.primary"
                  borderRadius="s"
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="12px"
                  fontFamily="mono"
                >
                  {`<SelectCell
  label="Teacher"
  value={selectedValue}
  onChange={(value) => setSelectedValue(value)}
  options={[
    { value: 'teacher', label: 'Teacher' },
    { value: 'student', label: 'Student' },
  ]}
/>`}
                </Code>
              </Box>

              {/* Minimal Table Example */}
              <Box bg="background.canvas" p="15px" borderRadius="l" mt="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="15px">
                  Example in a Table:
                </Text>
                {/* <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th textStyle="body.s.medium" color="text.primary" px="l" py="10px">
                          Name
                        </Th>
                        <Th textStyle="body.s.medium" color="text.primary" px="l" py="10px">
                          Email
                        </Th>
                        <Th textStyle="body.s.medium" color="text.primary" px="l" py="10px">
                          Role
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.primary">
                            Oliver Henderson
                          </Text>
                        </Td>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.secondary">
                            oliver@example.com
                          </Text>
                        </Td>
                        <Td px="l" py="5px">
                          <SelectCell
                            label={row1Role || 'Teacher'}
                            value={row1Role}
                            onChange={setRow1Role}
                            options={[
                              { value: 'teacher', label: 'Teacher' },
                              { value: 'student', label: 'Student' },
                              { value: 'admin', label: 'Admin' },
                            ]}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.primary">
                            Emma Smith
                          </Text>
                        </Td>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.secondary">
                            emma@example.com
                          </Text>
                        </Td>
                        <Td px="l" py="5px">
                          <SelectCell
                            label={row2Role || 'Student'}
                            value={row2Role}
                            onChange={setRow2Role}
                            options={[
                              { value: 'teacher', label: 'Teacher' },
                              { value: 'student', label: 'Student' },
                              { value: 'admin', label: 'Admin' },
                            ]}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.primary">
                            James Wilson
                          </Text>
                        </Td>
                        <Td px="l" py="10px">
                          <Text textStyle="body.s.medium" color="text.secondary">
                            james@example.com
                          </Text>
                        </Td>
                        <Td px="l" py="5px">
                          <SelectCell
                            label={row3Role || 'Teacher'}
                            value={row3Role}
                            onChange={setRow3Role}
                            options={[
                              { value: 'teacher', label: 'Teacher' },
                              { value: 'student', label: 'Student' },
                              { value: 'admin', label: 'Admin' },
                            ]}
                          />
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer> */}
              </Box>
            </VStack>

            {/* Segment Control - Standalone (Manual State Management) */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                Segment Control (Standalone)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Use this when you need full control over the state management
              </Text>
              <Box width="100%" maxWidth="521px">
                <SegmentControl
                  segments={segmentItems}
                  activeSegmentId={activeSegmentId}
                  onSegmentChange={setActiveSegmentId}
                />
              </Box>
              {activeSegment?.content && (
                <Box mt="l" pt="l" borderTop="default" borderColor="border.accent">
                  {activeSegment.content}
                </Box>
              )}
            </VStack>

            {/* Segment Control - With Content Wrapper (Simpler Usage) */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                Segment Control With Content (Wrapper Component)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Use this for simpler usage - state and content display are handled internally.
              </Text>
              <Box width="100%" maxWidth="521px">
                <SegmentControlWithContent
                  segments={segmentItems}
                  defaultActiveId="segment1"
                  keepMounted
                  onSegmentChange={(segmentId) => {
                    // eslint-disable-next-line no-console
                    console.log('Segment changed to:', segmentId);
                  }}
                />
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  Options:
                </Text>
                <VStack gap="xs" align="stretch">
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • <strong>keepMounted=true</strong> (default): Once visited, segments stay
                    mounted (preserves state when switching)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • <strong>keepMounted=false</strong>: Only active segment is mounted (saves
                    memory, loses state)
                  </Text>
                </VStack>
              </Box>
            </VStack>

            {/* NameList Component */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                NameList Component
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Displays a list of names with overflow handling. Shows the first name(s) and a
                &quot;+n&quot; indicator for remaining names. Hovering over &quot;+n&quot; shows a
                tooltip with all names.
              </Text>
              <VStack gap="l" align="flex-start" p="l" bg="background.canvas" borderRadius="l">
                <VStack gap="xs" align="flex-start">
                  <Text textStyle="body.xs.medium" color="text.secondary">
                    Single Name:
                  </Text>
                  <NameList names={['Jasper Green']} />
                </VStack>
                <VStack gap="xs" align="flex-start">
                  <Text textStyle="body.xs.medium" color="text.secondary">
                    Multiple Names (showing +2):
                  </Text>
                  <NameList names={['Jasper Green', 'Oliver Johnson', 'Ethan Brown']} />
                </VStack>
                <VStack gap="xs" align="flex-start">
                  <Text textStyle="body.xs.medium" color="text.secondary">
                    Many Names (showing +5):
                  </Text>
                  <NameList
                    names={[
                      'Jasper Green',
                      'Oliver Johnson',
                      'Ethan Brown',
                      'Liam Smith',
                      'Mason Davis',
                      'Noah Wilson',
                      'James Anderson',
                    ]}
                  />
                </VStack>
                <VStack gap="xs" align="flex-start">
                  <Text textStyle="body.xs.medium" color="text.secondary">
                    Custom maxVisible (showing first 2, then +3):
                  </Text>
                  <NameList
                    names={[
                      'Jasper Green',
                      'Oliver Johnson',
                      'Ethan Brown',
                      'Liam Smith',
                      'Mason Davis',
                    ]}
                    maxVisible={2}
                  />
                </VStack>
              </VStack>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  Props:
                </Text>
                <VStack gap="xs" align="stretch">
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • <strong>names</strong> (string[]): Array of names to display (required)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • <strong>maxVisible</strong> (number): Maximum number of names to show before
                    showing &quot;+n&quot; (default: 1)
                  </Text>
                </VStack>
              </Box>
            </VStack>

            {/* ChipInput Component - Standalone */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary" mt="l">
                ChipInput (Eigenständig)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Ein Texteingabefeld, das Chips aus der Eingabe erstellt. Chips werden bei Enter,
                Leertaste, Komma oder Semikolon erstellt. Unterstützt das Einfügen mehrerer
                Einträge, getrennt durch Trennzeichen. Passt sich automatisch in der Höhe an.
              </Text>
              <Box width="100%">
                <ChipInput
                  placeholder="Tags hinzufügen (Enter, Leertaste oder Komma)..."
                  values={chipValues}
                  onChange={setChipValues}
                  width="100%"
                  minWidth="100%"
                />
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                  Aktuelle Werte:
                </Text>
                <Code
                  p="10px"
                  bg="background.primary"
                  borderRadius="s"
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="12px"
                >
                  {JSON.stringify(chipValues, null, 2)}
                </Code>
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  Funktionen:
                </Text>
                <VStack gap="xs" align="stretch">
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Erstellt Chips bei <strong>Enter</strong>, <strong>Leertaste</strong>,{' '}
                    <strong>Komma</strong> oder <strong>Semikolon</strong>
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • <strong>Backspace</strong> entfernt den letzten Chip bei leerem Feld
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Unterstützt Einfügen mehrerer Einträge (Komma, Semikolon, Leertaste oder
                    Zeilenumbruch)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Passt sich automatisch in der Höhe an
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Erstellt Chip aus Resttext beim Verlassen des Feldes
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Konfigurierbare Breite und Mindestbreite
                  </Text>
                </VStack>
              </Box>
            </VStack>

            {/* EmailChipInput Component */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary" mt="l">
                EmailChipInput
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Ein Wrapper um ChipInput mit E-Mail-Validierung. Erstellt Chips nur für gültige
                E-Mail-Adressen. Unterstützt das Einfügen mehrerer E-Mails – gültige E-Mails werden
                sofort als Chips hinzugefügt, ungültige bleiben zur Korrektur im Feld.
              </Text>
              <Box width="100%">
                <EmailChipInput
                  placeholder="E-Mail-Adressen hinzufügen..."
                  values={emailChipValues}
                  onChange={setEmailChipValues}
                  width="100%"
                  minWidth="100%"
                />
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                  Aktuelle E-Mail-Werte:
                </Text>
                <Code
                  p="10px"
                  bg="background.primary"
                  borderRadius="s"
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="12px"
                >
                  {JSON.stringify(emailChipValues, null, 2)}
                </Code>
              </Box>
              <Box bg="background.canvas" p="15px" borderRadius="l">
                <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                  E-Mail-Funktionen:
                </Text>
                <VStack gap="xs" align="stretch">
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Prüft das Format vor dem Erstellen von Chips (RFC-5322-Regex)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Zeigt roten Rahmen und Fehlermeldung bei ungültigen E-Mails
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Unterstützt Einfügen mehrerer E-Mails (Komma, Semikolon, Leertaste oder
                    Zeilenumbruch)
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Beim Einfügen: gültige E-Mails werden sofort als Chips hinzugefügt, ungültige
                    bleiben zur Korrektur im Feld
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Ungültige E-Mails werden beim Verlassen gelöscht, wenn sie nicht korrigiert
                    wurden
                  </Text>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    • Verwendet <strong>type=&quot;email&quot;</strong> für bessere mobile
                    Tastaturunterstützung
                  </Text>
                </VStack>
              </Box>
            </VStack>

            {/* FormProvider + context field components (RHFFormInput, RHFChipInputField, …) */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                Mit React Hook Form (FormProvider + RHFFormInput / RHFChipInputField /
                RHFCheckboxField)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Die gemeinsame <Code fontSize="sm">Form</Code>-Komponente umschließt Kinder mit{' '}
                <Code fontSize="sm">FormProvider</Code>. Felder nutzen{' '}
                <Code fontSize="sm">useFormContext</Code> — an der Aufrufstelle reichen{' '}
                <Code fontSize="sm">name</Code> und UI-Props, ohne{' '}
                <Code fontSize="sm">register</Code> oder <Code fontSize="sm">control</Code>{' '}
                durchzureichen.
              </Text>
              <Form<RhfContextSampleValues, typeof rhfContextSampleSchema>
                onSubmit={handleRhfContextSampleSubmit}
                schema={rhfContextSampleSchema}
                options={{
                  defaultValues: {
                    displayName: '',
                    notes: [],
                    acceptTerms: false,
                  },
                }}
              >
                {({ watch }) => {
                  const ctxValues = watch();
                  return (
                    <VStack gap="l" align="stretch" maxW={{ md: '480px' }}>
                      <RHFFormInput<RhfContextSampleValues>
                        name="displayName"
                        label="Anzeigename"
                        placeholder="z. B. Alex"
                        clearable
                        leftElement={<UserIcon color="icon.quaternary" boxSize="16px" />}
                      />
                      <RHFChipInputField<RhfContextSampleValues>
                        name="notes"
                        label="Notizen (Chips)"
                        placeholder="Eintrag hinzufügen, Enter drücken…"
                      />
                      <RHFCheckboxField<RhfContextSampleValues>
                        name="acceptTerms"
                        labelElement={
                          <Text textStyle="body.m.regular" color="text.secondary">
                            Ich habe die Hinweise gelesen.
                          </Text>
                        }
                      />
                      <Box bg="background.canvas" p="15px" borderRadius="l">
                        <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                          Aktuelle Werte (watch):
                        </Text>
                        <Code
                          p="10px"
                          bg="background.primary"
                          borderRadius="s"
                          display="block"
                          whiteSpace="pre-wrap"
                          fontSize="12px"
                        >
                          {JSON.stringify(ctxValues, null, 2)}
                        </Code>
                      </Box>
                      <Button type="submit" variant="primary" w={{ base: 'full', md: 'auto' }}>
                        Absenden (Styleguide)
                      </Button>
                    </VStack>
                  );
                }}
              </Form>
            </VStack>

            {/* React Hook Form Example - MultiSelectField */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                Mit React Hook Form (MultiSelectField)
              </Text>
              <Form<FormValues, typeof formSchema> onSubmit={handleFormSubmit} schema={formSchema}>
                {({ control, formState, watch }) => {
                  const formValues = watch();
                  return (
                    <VStack gap="l" align="stretch">
                      <MultiSelectField
                        name="people"
                        control={control}
                        label="Personen Auswählen"
                        placeholder="Personen Suchen..."
                        options={sampleOptions}
                        error={formState.errors.people as FieldError}
                        searchable
                        secondaryKey="email"
                      />
                      <Box bg="background.canvas" p="15px" borderRadius="l">
                        <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                          Aktuelle Formularwerte:
                        </Text>
                        <Code
                          p="10px"
                          bg="background.primary"
                          borderRadius="s"
                          display="block"
                          whiteSpace="pre-wrap"
                          fontSize="12px"
                        >
                          {JSON.stringify(formValues, null, 2)}
                        </Code>
                      </Box>
                      {formState.errors.people && (
                        <Box
                          bg="background.canvas"
                          p="10px"
                          borderRadius="s"
                          borderLeft="3px solid"
                          borderColor="border.error"
                        >
                          <Text textStyle="body.xs.regular" color="text.error">
                            {formState.errors.people?.message}
                          </Text>
                        </Box>
                      )}
                      <Button type="submit" variant="primary">
                        Formular absenden
                      </Button>
                    </VStack>
                  );
                }}
              </Form>
            </VStack>

            {/* React Hook Form Example - ChipInputField */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                With React Hook Form (ChipInputField)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Form-compatible version with react-hook-form Controller integration. Handles
                validation and error states automatically via FieldWrapper.
              </Text>
              <Form<FormValues, typeof formSchema> onSubmit={handleFormSubmit} schema={formSchema}>
                {({ control, formState, watch }) => {
                  const formValues = watch();
                  return (
                    <VStack gap="l" align="stretch">
                      <ChipInputField
                        name="tags"
                        control={control}
                        label="Add Tags"
                        placeholder="Add tags, press Enter, Space, or Comma..."
                        error={formState.errors.tags as FieldError}
                      />
                      <Box bg="background.canvas" p="15px" borderRadius="l">
                        <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                          Current Form Values:
                        </Text>
                        <Code
                          p="10px"
                          bg="background.primary"
                          borderRadius="s"
                          display="block"
                          whiteSpace="pre-wrap"
                          fontSize="12px"
                        >
                          {JSON.stringify(formValues, null, 2)}
                        </Code>
                      </Box>
                      {formState.errors.tags && (
                        <Box
                          bg="background.canvas"
                          p="10px"
                          borderRadius="s"
                          borderLeft="3px solid"
                          borderColor="border.error"
                        >
                          <Text textStyle="body.xs.regular" color="text.error">
                            {(formState.errors.tags as FieldError)?.message}
                          </Text>
                        </Box>
                      )}
                      <Button type="submit" variant="primary">
                        Submit Form
                      </Button>
                    </VStack>
                  );
                }}
              </Form>
            </VStack>

            {/* React Hook Form Example - EmailChipInputField */}
            <VStack gap="15px" align="stretch">
              <Text textStyle="h3.medium" color="text.primary">
                With React Hook Form (EmailChipInputField)
              </Text>
              <Text textStyle="body.s.regular" color="text.tertiary">
                Form-compatible version with react-hook-form Controller integration. Combines
                component-level email validation with Joi schema validation. Handles validation and
                error states automatically via FieldWrapper.
              </Text>
              <Form<FormValues, typeof formSchema> onSubmit={handleFormSubmit} schema={formSchema}>
                {({ control, formState, watch }) => {
                  const formValues = watch();
                  return (
                    <VStack gap="l" align="stretch">
                      <EmailChipInputField
                        name="emails"
                        control={control}
                        label="Email Addresses"
                        placeholder="Add email addresses..."
                        error={formState.errors.emails as FieldError}
                      />
                      <Box bg="background.canvas" p="15px" borderRadius="l">
                        <Text textStyle="body.xs.medium" color="text.secondary" mb="5px">
                          Current Form Values:
                        </Text>
                        <Code
                          p="10px"
                          bg="background.primary"
                          borderRadius="s"
                          display="block"
                          whiteSpace="pre-wrap"
                          fontSize="12px"
                        >
                          {JSON.stringify(formValues, null, 2)}
                        </Code>
                      </Box>
                      {formState.errors.emails && (
                        <Box
                          bg="background.canvas"
                          p="10px"
                          borderRadius="s"
                          borderLeft="3px solid"
                          borderColor="border.error"
                        >
                          <Text textStyle="body.xs.regular" color="text.error">
                            {(formState.errors.emails as FieldError)?.message}
                          </Text>
                        </Box>
                      )}
                      <Box bg="background.canvas" p="15px" borderRadius="l">
                        <Text textStyle="body.xs.medium" color="text.secondary" mb="10px">
                          Features:
                        </Text>
                        <VStack gap="xs" align="stretch">
                          <Text textStyle="body.xs.regular" color="text.tertiary">
                            • Integrates with react-hook-form Controller for form state management
                          </Text>
                          <Text textStyle="body.xs.regular" color="text.tertiary">
                            • Dual validation: component-level (email regex) + Joi schema validation
                          </Text>
                          <Text textStyle="body.xs.regular" color="text.tertiary">
                            • Email format validated before creating chips (prevents invalid chips)
                          </Text>
                          <Text textStyle="body.xs.regular" color="text.tertiary">
                            • Error handling via FieldWrapper (shows label and error message)
                          </Text>
                          <Text textStyle="body.xs.regular" color="text.tertiary">
                            • Supports all EmailChipInput features (paste, validation, etc.)
                          </Text>
                        </VStack>
                      </Box>
                      <Button type="submit" variant="primary">
                        Submit Form
                      </Button>
                    </VStack>
                  );
                }}
              </Form>
            </VStack>
          </VStack>
        </Box>
      </Box>
    </PageContext>
  );
};
